<template>
  <div>
    <QuestionEdit
      v-model="enable_question_form"
      v-bind:editable="
        event.status !== 'completed' &&
        event.status !== 'archive' &&
        question_to_edit.getStatus() === 'draft'
      "
      v-bind:questionProp="question_to_edit"
      v-bind:quorum="event.quorum"
      v-on:question-update="handleQuestionUpdate"
    ></QuestionEdit>
    <v-text-field
      flat
      solo
      prepend-inner-icon="fa-search"
      v-model="search_query"
      :label="$t('fields.search')"
    ></v-text-field>
    <v-list three-line subheader style="background-color: inherit">
      <draggable
        v-model="questions"
        v-bind="dragOptions"
        @start="drag = true"
        @end="drag = false"
      >
        <transition-group type="transition" :name="!drag ? 'flip-list' : null">
          <v-list-tile
            v-for="question in questions"
            :key="question.id"
            avatar
            @click.stop="$emit('display-statistics', question)"
          >
            <v-list-tile-avatar>
              <v-icon :class="[question.iconClass]">{{ question.icon }}</v-icon>
            </v-list-tile-avatar>

            <v-list-tile-content>
              <v-list-tile-title class="break-all">{{
                question.question
              }}</v-list-tile-title>
              <v-list-tile-sub-title>{{
                question.subtitle
              }}</v-list-tile-sub-title>
              <v-list-tile-sub-title>{{
                question.subsubtitle
              }}</v-list-tile-sub-title>
            </v-list-tile-content>

            <v-list-tile-action
              v-if="
                event.status === 'active' &&
                ['draft', 'started'].includes(question.getStatus())
              "
            >
              <v-btn
                @click.stop="start(question)"
                icon
                ripple
                v-if="
                  event.status == 'active' &&
                  question.getStatus() == 'draft' &&
                  activeQuestion === null
                "
              >
                <v-icon color="green lighten-1">fa-play-circle</v-icon>
              </v-btn>
              <v-btn
                @click.stop="stop(question)"
                icon
                ripple
                v-if="
                  event.status == 'active' && question.getStatus() == 'started'
                "
              >
                <v-icon color="red lighten-1">fa-stop-circle</v-icon>
              </v-btn>
            </v-list-tile-action>
            <v-list-tile-action>
              <v-btn icon ripple @click.stop="openQuestionEditDialog(question)">
                <v-icon color="grey lighten-1">fa-edit</v-icon>
              </v-btn>
            </v-list-tile-action>
            <v-list-tile-action
              v-if="event.status !== 'completed' && event.status !== 'archive'"
            >
              <v-btn icon ripple @click.stop="duplicate(question)">
                <v-icon color="grey lighten-1">fa-copy</v-icon>
              </v-btn>
            </v-list-tile-action>
            <v-list-tile-action
              v-if="
                event.status !== 'completed' &&
                event.status !== 'archive' &&
                question.getStatus() === 'draft'
              "
            >
              <v-btn icon ripple @click.stop="destroy(question)">
                <v-icon color="grey lighten-1">fa-trash</v-icon>
              </v-btn>
            </v-list-tile-action>
          </v-list-tile>
        </transition-group>
      </draggable>
    </v-list>
  </div>
</template>

<script>
import QuestionEdit from '@/components/QuestionEdit.vue'
import Question from '@/models/Question.js'
import moment from 'moment'
import get_index_by_id from '@/helpers/get_index_by_id.js'
import handle_session_error from '@/helpers/handle_session_error.js'
import handle_bad_request_error from '@/helpers/handle_bad_request_error.js'
import get_question_order_number from '@/helpers/get_question_order_number.js'
import draggable from 'vuedraggable'

function format_date(value) {
  return moment(value).format('HH:mm')
}

// It returns "first" active question or null.
// Hint: There should only be one active question.
function find_active_question(questions) {
  for (let i = 0; i < questions.length; i++) {
    if (questions[i].isActive()) {
      return questions[i]
    }
  }

  return null
}

export default {
  components: {
    QuestionEdit,
    draggable,
  },

  props: ['event', 'editable'],

  data: () => ({
    enable_question_form: false,
    question_to_edit: new Question(),
    activeQuestion: null,

    search_query: '',
    drag: false,

    dragOptions: {
      animation: 200,
      group: 'questions',
      disabled: false,
      ghostClass: 'ghost',
    },
  }),

  computed: {
    questions: {
      get() {
        let sortBy = 'created_at'

        // if there is at least one question with order value not null, sort by order.
        if (
          this.event.questions.filter(question => {
            return question.order !== null
          }).length > 0
        ) {
          sortBy = 'order'
        }

        // Maps "extended" properties to instance of class Question.
        return this.event.questions
          .filter(question => {
            if (question.question.toLowerCase().includes(this.search_query)) {
              return true
            }
          })
          .map(question => {
            const status = question.getStatus()

            question.icon = 'fa-poll'
            question.iconClass =
              (status == 'started' ? 'success' : 'grey') + ' white--text'

            question.subtitle = this.$t(
              `components.questionList.${question.getStatus()}`
            )

            switch (status) {
              case 'started':
                question.subsubtitle = this.$t(
                  'components.questionList.startedText',
                  { value: format_date(question.started_at) }
                )
                break
              case 'stopped':
                question.subsubtitle = this.$t(
                  'components.questionList.stoppedText',
                  {
                    from: format_date(question.started_at),
                    to: format_date(question.stopped_at),
                  }
                )
                break

              default:
                question.subsubtitle = ''
                break
            }

            return question
          })
          .sort((a, b) => {
            // Puts the  active question on top.
            if (a.isActive()) return -1

            // Orders question from latest (top) to oldest (bottom)| from order X (top) to order 1 (bottom).
            return a[sortBy] > b[sortBy] ? -1 : a[sortBy] < b[sortBy] ? 1 : 0
          })
      },

      set(questions) {
        let counter = questions.length
        this.event.questions = questions.map(question => {
          question.order = counter--
          return question
        })

        if (
          this.event.status === 'active' ||
          this.event.status === 'published'
        ) {
          // Send new questions order to the api.
          this.$APICall(
            'put',
            'EVENTS',
            `/${this.event.id}/questions/update-order`,
            {
              ids: this.event.questions.map(question => {
                return question.id
              }),
            },
            () => {},
            false
          )
        }
      },
    },
  },

  created: function () {
    this.activeQuestion = find_active_question(this.event.questions)
  },

  methods: {
    start(question) {
      this.$verify(
        this.$t('components.questionList.verify.startQuestion'),
        result => {
          if (result) {
            let payload = {
              sms_authentication: this.event.sms_authentication,
              number_of_voters: this.event.voters.length,
            }
            this.$APICall(
              'post',
              'EVENTS',
              '/check-question-tokens',
              payload,
              data => {
                if (data.success) {
                  const index = get_index_by_id(
                    this.event.questions,
                    question.id
                  )

                  if (index !== false) {
                    this.$APICall(
                      'post',
                      'QUESTIONS',
                      '/' + question.id + '/start',
                      [],
                      data => {
                        this.event.questions.splice(
                          index,
                          1,
                          new Question(data.data)
                        )

                        this.activeQuestion = this.event.questions[index]
                      },
                      true,
                      error => {
                        handle_bad_request_error(
                          error,
                          this.$verify,
                          this.$i18n
                        )
                        handle_session_error(
                          error,
                          this.$store,
                          this.$router,
                          this.$prompt
                        )
                      }
                    )
                  } else {
                    throw new Error(
                      this.$t('components.questionList.errors.questionNotFound')
                    )
                  }
                } else {
                  this.$alert(this.$t('alerts.tokensNotAvailable'))
                }
              }
            )
          }
        }
      )
    },

    stop(question) {
      this.$verify(
        this.$t('components.questionList.verify.stopQuestion'),
        result => {
          if (result) {
            const index = get_index_by_id(this.event.questions, question.id)

            if (index !== false) {
              this.$APICall(
                'post',
                'QUESTIONS',
                '/' + question.id + '/stop',
                [],
                data => {
                  this.event.questions.splice(index, 1, new Question(data.data))

                  this.activeQuestion = null
                },
                true,
                error => {
                  handle_bad_request_error(error, this.$verify, this.$i18n)
                  handle_session_error(
                    error,
                    this.$store,
                    this.$router,
                    this.$prompt
                  )
                }
              )
            } else {
              throw new Error(
                this.$t('components.questionList.errors.questionNotFound')
              )
            }
          }
        }
      )
    },

    duplicate(question) {
      if (this.event.status === 'active' || this.event.status === 'published') {
        // Removes empty answers.
        // Warning: This is applied to the original question. Maybe clone it beore making changes.
        question.answers = question.answers.filter(answer => {
          return answer.label !== ''
        })

        this.$APICall(
          'post',
          'QUESTIONS',
          '',
          question,
          data => {
            this.event.questions.push(new Question(data.data))
          },
          true,
          error => {
            handle_session_error(error, this.$store, this.$router, this.$prompt)
          }
        )
      } else if (this.event.status === 'draft') {
        const cloned_question = new Question(question)

        // Generates the date in the same format as the API.
        cloned_question.created_at = new Date().toISOString()
        // This should be unique enough.
        cloned_question.id = cloned_question.created_at
        // Gets max order number or null.
        question.order = get_question_order_number(this.event.questions)

        this.event.questions.push(cloned_question)
      }
    },

    destroy(question) {
      const index = get_index_by_id(this.event.questions, question.id)

      if (index !== false) {
        this.event.questions.splice(index, 1)

        if (
          this.event.status === 'active' ||
          this.event.status === 'published'
        ) {
          this.$APICall(
            'delete',
            'QUESTIONS',
            '/' + question.id,
            [],
            () => {},
            true,
            error => {
              handle_session_error(
                error,
                this.$store,
                this.$router,
                this.$prompt
              )
            }
          )
        }
      } else {
        throw new Error(
          this.$t('components.questionList.errors.questionNotFound')
        )
      }
    },

    handleQuestionUpdate(question) {
      const index = get_index_by_id(
        this.event.questions,
        this.question_to_edit.id
      )

      if (index !== false) {
        if (
          this.event.status === 'active' ||
          this.event.status === 'published'
        ) {
          // Removes empty answers.
          question.answers = question.answers.filter(answer => {
            return answer.label !== ''
          })

          this.$APICall(
            'put',
            'QUESTIONS',
            '/' + question.id,
            question,
            data => {
              this.event.questions.splice(index, 1, new Question(data.data))
            },
            true,
            error => {
              handle_session_error(
                error,
                this.$store,
                this.$router,
                this.$prompt
              )
            }
          )
        } else {
          this.event.questions.splice(index, 1, question)
        }
      } else {
        throw new Error(
          this.$t('components.questionList.errors.questionNotFound')
        )
      }
    },

    openQuestionEditDialog(question) {
      this.question_to_edit = question
      this.enable_question_form = true
    },
  },
}
</script>

<style scoped>
/* Adds animation to questions when dragged. */
.flip-list-move {
  transition: transform 1s;
}

/* Maybe removes the animation. */
.no-move {
  transition: transform 0s;
}

/* When the question is being moved it makes it half transparent and sets the background color to primary. */
.ghost {
  opacity: 0.5;
  background: #88c143;
}

/* Sets right icons to white when dragged. */
.ghost i.v-icon.fa.theme--light.grey--text.text--lighten-1 {
  color: #ffffff !important;
}

/* Sets left icon to white on grey when dragged. */
.ghost i.v-icon.fa.theme--light.grey.white--text {
  color: #9e9e9e !important;
  background-color: #ffffff !important;
}

/* Sets list item height to auto, and applies padding for better fitment. */
::v-deep .v-list--three-line .v-list__tile {
  height: auto !important;
  padding: 8px 16px;
  min-height: 88px;
}

/* Sets list item title height to auto and wraps the title. */
::v-deep .v-list--three-line .v-list__tile__title {
  white-space: normal;
  height: auto;
  min-height: 24px;
}
</style>
